import { useState, useEffect } from "react";
import * as React from "react";
import { Modal } from "react-bootstrap";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import { getShiftName, compareShiftPersonLineRecItems, getPeopleShortNamesMap } from "@/lib/shifts";
import { getSectionedLines, toLineId } from "../lineLayout/lineLayoutEditor.lib";
import {
    classNames,
    dayString,
    fromShiftTag,
    getDateOfISOWeek,
    niceDate,
    niceDateTime,
    shiftNumber,
    toISODateString,
    weekToDayShift,
    stopKeyDownPropagation,
    getDay,
    type ILabelInputsPair,
} from "@/lib/util";
import { getLoggedinUser } from "@/lib/auth";
import { getLineGroupForUser, getPlantsForUser } from "@/lib/businessLogic";
import Tabs from "@/modules/shared/ui/Tabs";

import type { LineSection } from "../lineLayout/lineLayoutEditor.lib";
import type { IPersonData, IShiftPersonLineRec } from "@/lib/backend/manufacturing2.generated.types";

import "./ShiftAssignmentWeekPoolPrintModal.scss";

type PrintScope = keyof typeof PRINT_SCOPE;

export type PrintOptions = {
    scope: PrintScope;
    lines: IShiftPersonLineRec[];
};

const PRINT_SCOPE = {
    all: "all",
    shift_0: "shift_0",
    shift_1: "shift_1",
    shift_2: "shift_2",
    day_0: "day_0",
    day_1: "day_1",
    day_2: "day_2",
    day_3: "day_3",
    day_4: "day_4",
    day_5: "day_5",
    day_6: "day_6",
} as const;

const getPrintScopeTranslation = (scope: PrintScope): string => {
    const translations = {
        [PRINT_SCOPE.all]: translate("ShiftDetails.print_whole_week_mon_sun", "Whole week (Mon-Sun)"),
        [PRINT_SCOPE.shift_0]: translate("ShiftAssignmentPoolEdit.morning_shift", "Morning shift"),
        [PRINT_SCOPE.shift_1]: translate("ShiftAssignmentPoolEdit.afternoon_shift", "Afternoon shift"),
        [PRINT_SCOPE.shift_2]: translate("ShiftAssignmentPoolEdit.night_shift", "Night shift"),
        [PRINT_SCOPE.day_0]: dayString(0),
        [PRINT_SCOPE.day_1]: dayString(1),
        [PRINT_SCOPE.day_2]: dayString(2),
        [PRINT_SCOPE.day_3]: dayString(3),
        [PRINT_SCOPE.day_4]: dayString(4),
        [PRINT_SCOPE.day_5]: dayString(5),
        [PRINT_SCOPE.day_6]: dayString(6),
    };

    return translations[scope] || "";
};

export const PrintPage = (props: {
    year: number;
    week: number;
    plant_uuid: string;
    line_group_uuid: string;
    lines: IShiftPersonLineRec[];
    lineSections: LineSection[];
    logo: string;
    options: PrintOptions;
    people: IPersonData[];
    getHolidaysForDate: (date: string) => string[];
}) => {
    const { lines, line_group_uuid, lineSections, plant_uuid, logo, options, people, week, year } = props;
    const people_names_map = getPeopleShortNamesMap(people);
    const plant = getPlantsForUser().find(plant => plant.uuid === plant_uuid) || null;
    const line_group = getLineGroupForUser(line_group_uuid);
    const sectionedLines = getSectionedLines(lines, lineSections, line => toLineId(line.line_id));
    const user = getLoggedinUser();
    const now = new Date();

    const getTitle = (): string => {
        let title = translate("common.timetable", "Time-table");
        title += ` (${translate("common.week", "week")} ${week}, ${year})`;
        const location: string[] = [];
        if (plant !== null) {
            location.push(plant.title);
        }

        if (line_group !== null) {
            location.push(line_group.title);
        }

        if (location.length > 0) {
            title += `: ${location.join(" - ")}`;
        }

        return title;
    };

    const isShiftSelected = (shift_tag: string): boolean => {
        if (options.scope === PRINT_SCOPE.all) {
            return true;
        }

        const shift_number = shiftNumber(fromShiftTag(shift_tag));
        if (options.scope === PRINT_SCOPE[`shift_${shift_number.shift_day}`]) {
            return true;
        }

        if (options.scope === PRINT_SCOPE[`day_${shift_number.day_number}`]) {
            return true;
        }

        return false;
    };

    const renderHeader = () => {
        if (lines.length === 0) {
            return null;
        }

        const shifts = lines[0].shifts
            .filter(shift => isShiftSelected(shift.shift_tag))
            .sort(compareShiftPersonLineRecItems);

        if (shifts.length === 0) {
            return null;
        }

        const shift_scopes: PrintScope[] = [PRINT_SCOPE.shift_0, PRINT_SCOPE.shift_1, PRINT_SCOPE.shift_2];
        const day_scopes: PrintScope[] = [
            PRINT_SCOPE.day_0,
            PRINT_SCOPE.day_1,
            PRINT_SCOPE.day_2,
            PRINT_SCOPE.day_3,
            PRINT_SCOPE.day_4,
            PRINT_SCOPE.day_5,
            PRINT_SCOPE.day_6,
        ];
        const is_shift_scope = shift_scopes.includes(options.scope);
        const is_day_scope = day_scopes.includes(options.scope);
        const header: React.ReactNode[] = [];

        if (options.scope === PRINT_SCOPE.all) {
            header.push(
                <tr key="all">
                    {shift_scopes.map((scope, i) => (
                        <th key={scope} className={`shift-${i}`} colSpan={7}>
                            <span>{getPrintScopeTranslation(scope)}</span>
                        </th>
                    ))}
                </tr>
            );
        } else if (is_shift_scope) {
            header.push(
                <tr key="shift">
                    <th key={options.scope} className={`shift-${weekToDayShift(shifts[0].shift_number)}`} colSpan={7}>
                        <span>{getPrintScopeTranslation(options.scope)}</span>
                    </th>
                </tr>
            );
        } else {
            const date = fromShiftTag(shifts[0].shift_tag);
            const holidays = props.getHolidaysForDate(toISODateString(date));
            header.push(
                <tr key="day" className={classNames({ "is-holiday": holidays.length > 0 })}>
                    <th colSpan={3}>
                        <span>
                            {getPrintScopeTranslation(options.scope)}, {date.getDate()}.{" "}
                            {translate(`common.month${date.getMonth()}`).slice(0, 3)}
                        </span>
                    </th>
                </tr>
            );
        }

        header.push(
            <tr key="shifts">
                {shifts.map(shift => {
                    const date = fromShiftTag(shift.shift_tag);
                    const holidays = props.getHolidaysForDate(toISODateString(date));
                    return (
                        <th
                            key={shift.shift_tag}
                            className={classNames(`shift-${weekToDayShift(shift.shift_number)}`, {
                                "is-holiday": holidays.length > 0 && !is_day_scope,
                            })}
                        >
                            {is_day_scope ? (
                                <span>{translate(getShiftName(weekToDayShift(shift.shift_number)))}</span>
                            ) : (
                                <span>
                                    {dayString(getDay(date)).slice(0, 3)}, {date.getDate()}.{" "}
                                    {translate(`common.month${date.getMonth()}`).slice(0, 3)}
                                </span>
                            )}
                        </th>
                    );
                })}
            </tr>
        );

        return header;
    };

    return (
        <html>
            <head>
                <title>{getTitle()}</title>
                <style>{`
                    body {
                        margin: 0;
                        padding: 0;
                        font-family: Arial, Helvetica, sans-serif;
                    }

                    table {
                        width: 100%;
                        border-collapse: collapse;
                        border-spacing: 0;
                        font-size: 14px;
                    }

                    th, td {
                        padding: 4px;
                        border: 1px solid #000;
                    }

                    tr:first-child th:first-child {
                        border-right-width: 2px;
                        border-bottom-width: 2px;
                    }

                    tr:nth-child(2) th {
                        border-right-width: 2px;
                    }

                    tr:nth-child(3) th {
                        border-bottom-width: 2px;
                    }

                    tr:nth-child(3) th:nth-child(7n) {
                        border-right-width: 2px;
                    }

                    td:first-child {
                        font-weight: bold;
                        border-right-width: 2px;
                    }

                    td:nth-child(7n+1) {
                        border-right-width: 2px;
                    }

                    th:last-child,
                    td:last-child {
                        border-right-width: 1px !important;
                    }

                    td.is-disabled {
                        background-size: 4px 4px;
                        background-image: repeating-linear-gradient(45deg, hsl(0deg, 0%, 70%) 0, hsl(0deg, 0%, 70%) 0.6px, hsl(216deg, 33%, 91%) 0, hsl(216deg, 33%, 91%) 50%);
                    }

                    td ul {
                        list-style: none;
                        padding: 0;
                        margin: 0;
                    }

                    th span,
                    td span {
                        white-space: nowrap;
                    }

                    .text-left {
                        text-align: left;
                    }

                    .logo {
                        width: 100px;
                    }

                    .author {
                        margin-bottom: 4px;
                        font-size: 12px;
                        text-align: right;
                    }

                    .shift-0 {
                        background-color: rgba(247, 237, 67, 0.5019607843);
                    }

                    .shift-1 {
                        background-color: rgba(249, 196, 81, 0.5019607843);
                    }

                    .shift-2 {
                        background-color: rgba(35, 110, 211, 0.5019607843);
                    }

                    .is-holiday {
                        background-color: rgba(248, 106, 106, 0.5019607843);
                    }

                    .section-header {
                        background-color: #bbc3ca;
                    }

                    .has-divider-after {
                        border-bottom: 3px double #000;
                    }
                `}</style>
                <script>{`
                    window.onload = function() {
                        window.print();
                    };
                `}</script>
            </head>
            <body>
                <p className="author">
                    {user.title}, {niceDateTime(now)}
                </p>
                <table>
                    <thead>
                        <tr>
                            <th className="text-left" rowSpan={3}>
                                {logo && <img className="logo" src={logo} />}
                            </th>
                            <th colSpan={21}>{getTitle()}</th>
                        </tr>
                        {renderHeader()}
                    </thead>
                    <tbody>
                        {sectionedLines.map(({ line, sectionHeader, showDividerAfter }) => {
                            const shifts = line.shifts.filter(shift => isShiftSelected(shift.shift_tag));
                            shifts.sort(compareShiftPersonLineRecItems);
                            return (
                                <React.Fragment key={line.line_uuid}>
                                    {sectionHeader && (
                                        <tr key={`section-${sectionHeader.id}`} className="section-header">
                                            <td colSpan={shifts.length + 1}>{sectionHeader.title}</td>
                                        </tr>
                                    )}
                                    <tr key={line.line_uuid}>
                                        <td className={classNames({ "has-divider-after": showDividerAfter })}>
                                            {line.line_title}
                                        </td>
                                        {shifts.map(shift => {
                                            const date = fromShiftTag(shift.shift_tag);
                                            const holidays = props.getHolidaysForDate(toISODateString(date));
                                            return (
                                                <td
                                                    key={shift.shift_tag}
                                                    className={classNames({
                                                        "is-disabled": !shift.enabled,
                                                        "is-holiday": holidays.length > 0,
                                                        "has-divider-after": showDividerAfter,
                                                    })}
                                                >
                                                    <ul>
                                                        {shift.persons.map(person => (
                                                            <li key={person.uuid}>
                                                                <span>
                                                                    {people_names_map.get(person.uuid) || person.name}
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </body>
        </html>
    );
};

const ScopeTabHeader = () => {
    const title = translate("common.scope", "Scope");
    return (
        <span title={title}>
            <i className="fas fa-calendar" />
            <span>{title}</span>
        </span>
    );
};

const LinesTabHeader = (props: { print_lines: boolean[] }) => {
    const { print_lines } = props;
    const title = translate("common.lines", "Lines");

    return (
        <span title={title}>
            <i className="fas fa-list" />
            <span>
                {title} ({print_lines.filter(Boolean).length} / {print_lines.length})
            </span>
        </span>
    );
};

const ScopeTabContent = (props: {
    print_scope: PrintScope;
    year: number;
    week: number;
    onPrintScopeChange: (value: PrintScope) => void;
}) => {
    const { print_scope, year, week, onPrintScopeChange } = props;

    const getDate = (year: number, week: number, day: number): Date => {
        const date = getDateOfISOWeek(week, year);
        date.setDate(date.getDate() + day);
        return date;
    };

    const fieldsets: ILabelInputsPair[] = [
        {
            label: translate("common.all", "All"),
            inputs: [
                {
                    value: PRINT_SCOPE.all,
                    label: translate("ShiftDetails.print_whole_week_mon_sun", "Whole week (Mon-Sun)"),
                },
            ],
        },
        {
            label: translate("common.shift", "Shift"),
            inputs: [PRINT_SCOPE.shift_0, PRINT_SCOPE.shift_1, PRINT_SCOPE.shift_2].map(scope => ({
                value: scope,
                label: `${getPrintScopeTranslation(scope)} (${dayString(0).slice(0, 3)} - ${dayString(6).slice(0, 3)})`,
            })),
        },
        {
            label: translate("common.day", "Day"),
            inputs: [
                PRINT_SCOPE.day_0,
                PRINT_SCOPE.day_1,
                PRINT_SCOPE.day_2,
                PRINT_SCOPE.day_3,
                PRINT_SCOPE.day_4,
                PRINT_SCOPE.day_5,
                PRINT_SCOPE.day_6,
            ].map((scope, index) => ({
                value: scope,
                label: `${getPrintScopeTranslation(scope)} (${niceDate(getDate(year, week, index))})`,
            })),
        },
    ];

    return (
        <>
            {fieldsets.map((fieldset, fieldset_index) => (
                <fieldset key={fieldset_index} className="form-group">
                    <legend className="h6 pt-0">{fieldset.label}</legend>
                    {fieldset.inputs.map(input => (
                        <div key={input.value} className="form-check">
                            <label className="form-check-label">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="scope"
                                    value={input.value}
                                    checked={print_scope === input.value}
                                    onChange={() => onPrintScopeChange(input.value as PrintScope)}
                                />
                                <span>{input.label}</span>
                            </label>
                        </div>
                    ))}
                </fieldset>
            ))}
        </>
    );
};

const LinesTabContent = (props: {
    pooled_lines: IShiftPersonLineRec[];
    print_lines: boolean[];
    onPrintLinesChange: (index: number) => void;
}) => {
    const { pooled_lines, print_lines, onPrintLinesChange } = props;

    return (
        <div>
            <fieldset className="form-group">
                {pooled_lines.map((line, index) => (
                    <div
                        className="form-check form-check--border form-check--padding"
                        key={index}
                        title={line.line_title}
                    >
                        <label className="form-check-label form-check-label--truncate">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                name="print_lines"
                                value={line.line_uuid}
                                checked={print_lines[index]}
                                onChange={() => onPrintLinesChange(index)}
                            />
                            {line.line_title}
                        </label>
                    </div>
                ))}
            </fieldset>
        </div>
    );
};

export const PrintModal = (props: {
    show: boolean;
    week: number;
    year: number;
    pooled_lines: IShiftPersonLineRec[];
    saved_settings?: { scope: PrintScope; line_uuids: string[] };
    onClose: () => void;
    onPrint: (options: { scope: PrintScope; lines: IShiftPersonLineRec[]; print_lines: boolean[] }) => Promise<void>;
}) => {
    const { show, week, year, pooled_lines, saved_settings, onClose, onPrint } = props;

    const [print_scope, setPrintScope] = useState<PrintScope>(PRINT_SCOPE.all);
    const [print_lines, setPrintLines] = useState<boolean[]>([]);

    useEffect(() => {
        if (pooled_lines.length === 0) {
            return;
        }

        if (saved_settings) {
            const { scope, line_uuids } = saved_settings;
            const line_uuids_set = new Set(line_uuids);
            const print_lines = pooled_lines.map(pooled_line => line_uuids_set.has(pooled_line.line_uuid));
            setPrintScope(scope);
            setPrintLines(print_lines);
        } else {
            setPrintLines(pooled_lines.map(() => true));
        }
    }, [pooled_lines, saved_settings]);

    const handlePrintScopeChange = (value: PrintScope): void => {
        setPrintScope(value);
    };

    const handlePrintLinesChange = (index: number): void => {
        const cloned_lines = [...print_lines];
        cloned_lines[index] = !cloned_lines[index];
        setPrintLines(cloned_lines);
    };

    const form_id = "shift-assignment-week-pool-print-form";

    const handleSubmit = () => (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const lines = pooled_lines.filter((_, index) => print_lines[index]);

        if (print_scope && lines.length > 0) {
            onPrint({ scope: print_scope, lines, print_lines });
            onClose();
        }
    };

    return (
        <Modal
            className="shift-assignment-week-pool__print-modal"
            dialogClassName="modal-dialog-scrollable"
            show={show}
            onHide={onClose}
            animation={false}
            onKeyDown={stopKeyDownPropagation}
        >
            <Modal.Header>
                <Modal.Title>{translate("common.print", "Print")}</Modal.Title>
                <button type="button" className="close" onClick={onClose}>
                    <span>×</span>
                </button>
            </Modal.Header>
            <Modal.Body>
                <form id={form_id} action="" onSubmit={handleSubmit()}>
                    <Tabs
                        data={[
                            {
                                title: <ScopeTabHeader />,
                                id: "scope",
                                content: (
                                    <ScopeTabContent
                                        print_scope={print_scope}
                                        year={year}
                                        week={week}
                                        onPrintScopeChange={handlePrintScopeChange}
                                    />
                                ),
                            },
                            {
                                title: <LinesTabHeader print_lines={print_lines} />,
                                id: "lines",
                                content: (
                                    <LinesTabContent
                                        pooled_lines={pooled_lines}
                                        print_lines={print_lines}
                                        onPrintLinesChange={handlePrintLinesChange}
                                    />
                                ),
                            },
                        ]}
                        active_tab={"scope"}
                    />
                </form>
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-outline-secondary" onClick={onClose}>
                    {translate("common.cancel", "Cancel")}
                </button>
                <button className="btn btn-primary" form={form_id}>
                    {translate("common.print", "Print")}
                </button>
            </Modal.Footer>
        </Modal>
    );
};
